import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PropTypes from 'prop-types';

import { moveRelayFaxOrderToPom } from 'store/thunks/workQueueThunks';

import {
  FAX_MAY_BE_AT_PHARMACY_CODES,
  relayFaxDestination,
} from '../constants/nonRoutableRequests';
import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { MOVE_TO_POM_OUTCOME_COPY } from '../utils/moveToPomOutcomeCopy';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';

const successMessage = () =>
  'Moved to POM. The fax relay was cancelled and POM will fill this as a normal order.';

const withMoveCopy = (outcome) =>
  outcome && {
    ...outcome,
    described: { ...outcome.described, ...MOVE_TO_POM_OUTCOME_COPY[outcome.described.code] },
  };

const MoveRelayFaxToPomDialog = ({ open, onClose, item }) => {
  const { submit, submitting, outcome, dismiss } = useNonRoutableResolution({
    item,
    action: moveRelayFaxOrderToPom,
    successMessage,
    onClose,
    open,
  });

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  const canSubmit = !outcome || nonRoutableRetryIsSafe(outcome.described);
  const atRisk = FAX_MAY_BE_AT_PHARMACY_CODES.has(item.relayErrorCode);
  const pharmacy = relayFaxDestination(item);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='MoveRelayFaxToPomDialog-title'
    >
      <DialogTitle id='MoveRelayFaxToPomDialog-title'>Move this order to POM?</DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        {atRisk && (
          <Alert severity='error' data-testid='move-to-pom-fax-may-be-at-pharmacy'>
            <AlertTitle>The pharmacy may already have this fax</AlertTitle>
            {`The fax may already have reached ${pharmacy}. Confirm with the pharmacy that they ` +
              'will not fill this prescription before moving it to POM.'}
          </Alert>
        )}

        <Alert severity='warning' sx={{ mt: atRisk ? 2 : 0 }}>
          <AlertTitle>POM will fill this as a normal order</AlertTitle>
          {`The fax relay to ${pharmacy} will be cancelled and the order will ` +
            'be filled by POM as a normal order. It will be dispensed, and this cannot be undone.'}
        </Alert>

        {outcome && <NonRoutableResolutionOutcome outcome={withMoveCopy(outcome)} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {outcome && !canSubmit ? 'Close' : 'Leave it parked'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='error'
            loading={submitting}
            onClick={submit}
            id={`MoveRelayFaxToPomDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Move to POM
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

MoveRelayFaxToPomDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    destinationName: PropTypes.string,
    relayErrorCode: PropTypes.string,
  }).isRequired,
};

export default MoveRelayFaxToPomDialog;
